"use client";

import type { ThemeProviderProps } from "next-themes";

import * as React from "react";
import { HeroUIProvider } from "@heroui/system";
import { ToastProvider } from "@heroui/toast";
import { useRouter } from "next/navigation";
import { ThemeProvider as NextThemesProvider } from "next-themes";

import { AuthProvider } from "@/components/providers/AuthProvider";
import { LibraryProvider } from "@/components/providers/LibraryProvider";

export interface ProvidersProps {
  children: React.ReactNode;
  themeProps?: ThemeProviderProps;
}

declare module "@react-types/shared" {
  interface RouterConfig {
    routerOptions: NonNullable<
      Parameters<ReturnType<typeof useRouter>["push"]>[1]
    >;
  }
}

export function Providers({ children, themeProps }: ProvidersProps) {
  const router = useRouter();

  return (
    // reducedMotion="user": animations follow the OS "reduce motion" setting
    <HeroUIProvider navigate={router.push} reducedMotion="user">
      <NextThemesProvider {...themeProps}>
        <AuthProvider>
          <LibraryProvider>{children}</LibraryProvider>
        </AuthProvider>
        {/* Anyone may call `addToast()` from "@heroui/toast". */}
        <ToastProvider
          maxVisibleToasts={3}
          placement="bottom-center"
          toastOffset={16}
          toastProps={{
            classNames: {
              // Clear of the home indicator on phones.
              motionDiv: "mb-[calc(0.25rem+env(safe-area-inset-bottom))]",
              // Wrap long messages instead of cutting them off.
              title: "whitespace-normal",
            },
          }}
        />
      </NextThemesProvider>
    </HeroUIProvider>
  );
}
